import { cn } from '@/lib/utils';

interface FinanceV2DataTableProps {
  rows: Record<string, unknown>[];
  columns: string[];
  summaryRows?: Array<{
    label: string;
    values: Record<string, string | number>;
  }>;
  density?: 'compact' | 'comfortable';
  emptyMessage?: string;
  maxHeight?: string;
  /** Keep vertical scrolling with this table, or delegate it to a parent surface. */
  scrollAxes?: 'both' | 'horizontal';
  getColumnLabel?: (column: string) => string;
  formatCell?: (value: unknown, column: string, row: Record<string, unknown>) => string;
}

function defaultFormatCell(value: unknown): string {
  if (value == null || value === '') return '-';
  if (typeof value === 'number') {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function FinanceV2DataTable({
  rows,
  columns,
  summaryRows = [],
  density = 'comfortable',
  emptyMessage = 'No tabular data to display.',
  maxHeight = '320px',
  scrollAxes = 'both',
  getColumnLabel,
  formatCell = defaultFormatCell,
}: FinanceV2DataTableProps) {
  const padY = density === 'compact' ? 'py-1.5' : 'py-2.5';

  if (rows.length === 0 || columns.length === 0) {
    return (
      <div className="rounded-[10px] border border-[#EDE6E1] bg-white p-6 text-center text-[12px] italic text-[#7B6B72]">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[10px] border border-[#EDE6E1] bg-white">
      <div
        data-table-scroll="true"
        className={cn(
          'scroll-thin',
          scrollAxes === 'horizontal' ? 'overflow-x-auto overflow-y-hidden' : 'overflow-auto',
        )}
        style={{ maxHeight: scrollAxes === 'both' ? maxHeight : undefined }}
      >
        <table className="w-full min-w-full border-collapse text-[12px]">
          <thead className="sticky top-0 z-20 bg-[#FAF7F1]">
            <tr>
              {columns.map((column, columnIndex) => (
                <th
                  key={column}
                  className={cn(
                    "sticky top-0 z-20 whitespace-nowrap border-b border-r border-[#EDE6E1] bg-[#FAF7F1] px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[#7B6B72] last:border-r-0",
                    columnIndex > 0 && "text-right"
                  )}
                >
                  {getColumnLabel?.(column) ?? column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={cn(
                  "border-t border-[#EDE6E1] transition-colors hover:bg-[#f5e9ee]/40",
                  rowIndex % 2 === 0 ? "bg-white" : "bg-[#FAF7F1]/40"
                )}
              >
                {columns.map((column, columnIndex) => {
                  const value = row[column];
                  const displayValue = formatCell(value, column, row);
                  const isNumber = typeof value === 'number';

                  return (
                    <td
                      key={column}
                      className={cn(
                        "border-r border-[#EDE6E1] px-3 align-middle text-[#3F3338] last:border-r-0",
                        padY,
                        columnIndex === 0 && "font-medium text-[#1F1A20]",
                        isNumber && "text-right font-mono tabular-nums text-[#1F1A20]",
                        isNumber && value < 0 && "text-[#B23A48]",
                        (value == null || value === '') && "text-[#A8989F]"
                      )}
                      title={displayValue}
                    >
                      <span className="block max-w-[260px] truncate">{displayValue}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {summaryRows.length > 0 && (
            <tfoot className="border-t-2 border-[#D9CFC4] bg-[#FAF7F1]">
              {summaryRows.map((summary) => (
                <tr key={summary.label} className="border-t border-[#EDE6E1]">
                  {columns.map((column, columnIndex) => {
                    const displayValue = columnIndex === 0
                      ? summary.label
                      : summary.values[column] ?? '-';

                    return (
                      <td
                        key={column}
                        className={cn(
                          "border-r border-[#EDE6E1] px-3 align-middle text-[12px] last:border-r-0",
                          padY,
                          columnIndex === 0
                            ? "font-display font-semibold uppercase tracking-[0.12em] text-[#7B6B72]"
                            : "text-right font-mono tabular-nums text-[#1F1A20]"
                        )}
                      >
                        {displayValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
